import type { GeoPoint, PolygonCoordinates, ZoneGeometry } from '../api/endpoints';

/**
 * Координаты между тремя представлениями: GeoJSON сервера, карта Яндекса и поля формы.
 *
 * Порядок осей — главная ловушка: GeoJSON (и PostGIS) пишет `[lng, lat]`, Яндекс Карты
 * по умолчанию — `[lat, lng]`. Перепутанный порядок не даёт ошибки: зона Ташкента
 * молча ложится в Индийский океан, и адреса перестают попадать в неё. Поэтому
 * преобразование — только здесь и только через эти функции, покрытые тестом.
 *
 * Кольцо GeoJSON замкнуто (первая точка повторена последней, RFC 7946 §3.1.6); в
 * редакторе карты контур хранится без повтора. Замыкание — при отправке, размыкание —
 * при отрисовке.
 */

/** Точка в порядке карты: `[широта, долгота]`. */
export type LatLng = readonly [number, number];

/** Контур полигона на карте: внешний контур первым, дальше — вырезы. */
export type Contours = readonly (readonly LatLng[])[];

/**
 * Точность координат при отправке: шесть знаков — около 10 см. Больше — шум
 * перетаскивания мышью, который раздувает тело запроса и журнал аудита.
 */
const DECIMALS = 6;
const DECIMAL_BASE = 10;
const SCALE = DECIMAL_BASE ** DECIMALS;
/** Пара GeoJSON: долгота и широта (третья координата — высота — не используется). */
const PAIR = 2;

/** Минимум различных вершин контура: меньше трёх — отрезок, а не площадь. */
export const MIN_RING_POINTS = 3;

const LAT_LIMIT = 90;
const LNG_LIMIT = 180;

export function roundCoordinate(value: number): number {
  return Math.round(value * SCALE) / SCALE;
}

/** Кольцо GeoJSON → контур карты: оси меняются местами, замыкающая точка снимается. */
export function ringToContour(ring: readonly (readonly number[])[]): LatLng[] {
  const points = ring
    .filter((pair) => pair.length >= PAIR)
    .map((pair) => [Number(pair[1]), Number(pair[0])] as const);

  return dropClosing(points);
}

/** Контур карты → кольцо GeoJSON: оси меняются местами, кольцо замыкается. */
export function contourToRing(contour: readonly LatLng[]): [number, number][] {
  const open = dropClosing(dedupe(contour));
  const ring = open.map(
    ([lat, lng]) => [roundCoordinate(lng), roundCoordinate(lat)] as [number, number],
  );
  const first = ring[0];

  return first === undefined ? [] : [...ring, [first[0], first[1]]];
}

/**
 * Контуры редактора → геометрия зоны для `ZoneInput.geometry`.
 *
 * Контур короче трёх различных вершин отбрасывается: редактор оставляет такие после
 * неудачного рисования. Если отброшен внешний контур — геометрии нет вовсе: вырезы без
 * внешнего контура не описывают никакую площадь.
 */
export function geometryFromContours(contours: Contours): ZoneGeometry | null {
  const [outer, ...holes] = contours;

  if (outer === undefined || !isUsableContour(outer)) {
    return null;
  }

  return {
    type: 'Polygon',
    coordinates: [outer, ...holes.filter(isUsableContour)].map(contourToRing),
  };
}

/** Геометрия зоны → полигоны карты (у `MultiPolygon` их несколько). */
export function contoursFromGeometry(geometry: ZoneGeometry | null | undefined): Contours[] {
  if (geometry === null || geometry === undefined || !Array.isArray(geometry.coordinates)) {
    return [];
  }

  const polygons: readonly PolygonCoordinates[] =
    geometry.type === 'MultiPolygon'
      ? geometry.coordinates
      : geometry.type === 'Polygon'
        ? [geometry.coordinates]
        : [];

  return polygons
    .map((polygon) => polygon.map((ring) => ringToContour(ring)))
    .filter((contours) => contours[0] !== undefined && isUsableContour(contours[0]));
}

/** Число вершин внешнего контура (или всех частей мультиполигона) — для подписи в форме. */
export function vertexCount(geometry: ZoneGeometry | null | undefined): number {
  return contoursFromGeometry(geometry).reduce(
    (sum, contours) => sum + (contours[0]?.length ?? 0),
    0,
  );
}

export function isUsableContour(contour: readonly LatLng[]): boolean {
  return dropClosing(dedupe(contour)).length >= MIN_RING_POINTS;
}

/**
 * Разбор GeoJSON, вставленного руками — запасной путь, когда карта недоступна (ключ не
 * задан или не разрешён для домена). Принимает геометрию, `Feature` и
 * `FeatureCollection` с одной геометрией: именно это отдают geojson.io и выгрузки
 * ГИС. `null` — текст не описывает полигон.
 */
export function parseGeometryText(text: string): ZoneGeometry | null {
  let value: unknown;

  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }

  const geometry = unwrapGeometry(value);

  if (geometry === null) {
    return null;
  }

  if (geometry.type === 'Polygon' && isPolygonCoordinates(geometry.coordinates)) {
    const contours = geometry.coordinates.map((ring) => ringToContour(ring));

    return geometryFromContours(contours);
  }

  if (
    geometry.type === 'MultiPolygon' &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length > 0 &&
    geometry.coordinates.every(isPolygonCoordinates)
  ) {
    const parts = (geometry.coordinates as PolygonCoordinates[])
      .map((polygon) => geometryFromContours(polygon.map((ring) => ringToContour(ring))))
      .filter((part): part is Extract<ZoneGeometry, { type: 'Polygon' }> => part !== null);

    if (parts.length === 0) {
      return null;
    }

    const [single] = parts;

    return parts.length === 1 && single !== undefined
      ? single
      : { type: 'MultiPolygon', coordinates: parts.map((part) => part.coordinates) };
  }

  return null;
}

/**
 * Точка из полей формы. Пустое, нечисловое или за пределами диапазона — `null`.
 * Запятая принимается как десятичный разделитель: так координаты копируются из
 * русскоязычных таблиц.
 */
export function pointFromInputs(lat: string, lng: string): GeoPoint | null {
  const parsedLat = parseCoordinate(lat);
  const parsedLng = parseCoordinate(lng);

  if (parsedLat === null || parsedLng === null) {
    return null;
  }

  if (Math.abs(parsedLat) > LAT_LIMIT || Math.abs(parsedLng) > LNG_LIMIT) {
    return null;
  }

  return { lat: roundCoordinate(parsedLat), lng: roundCoordinate(parsedLng) };
}

export function parseCoordinate(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');

  if (trimmed === '' || !/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : null;
}

/** Координата в поле формы: без хвоста нулей и без экспоненты. */
export function formatCoordinate(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(roundCoordinate(value));
}

function unwrapGeometry(value: unknown): { type: string; coordinates: unknown } | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;

  if (record['type'] === 'FeatureCollection' && Array.isArray(record['features'])) {
    return record['features'].length === 1 ? unwrapGeometry(record['features'][0]) : null;
  }

  if (record['type'] === 'Feature') {
    return unwrapGeometry(record['geometry']);
  }

  return typeof record['type'] === 'string'
    ? { type: record['type'], coordinates: record['coordinates'] }
    : null;
}

function isPolygonCoordinates(value: unknown): value is PolygonCoordinates {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (ring) =>
        Array.isArray(ring) &&
        ring.every(
          (pair) =>
            Array.isArray(pair) &&
            pair.length >= PAIR &&
            typeof pair[0] === 'number' &&
            typeof pair[1] === 'number' &&
            Math.abs(pair[0]) <= LNG_LIMIT &&
            Math.abs(pair[1]) <= LAT_LIMIT,
        ),
    )
  );
}

function samePoint(left: LatLng, right: LatLng): boolean {
  return (
    roundCoordinate(left[0]) === roundCoordinate(right[0]) &&
    roundCoordinate(left[1]) === roundCoordinate(right[1])
  );
}

/** Подряд идущие одинаковые вершины — след двойного щелчка в редакторе. */
function dedupe(contour: readonly LatLng[]): LatLng[] {
  return contour.filter((point, index) => {
    const previous = contour[index - 1];

    return previous === undefined || !samePoint(previous, point);
  });
}

function dropClosing(contour: readonly LatLng[]): LatLng[] {
  const first = contour[0];
  const last = contour[contour.length - 1];

  return contour.length > 1 && first !== undefined && last !== undefined && samePoint(first, last)
    ? contour.slice(0, -1)
    : [...contour];
}
